import * as React from 'react'
import { getAllSectionsBase, getSection } from '@/requests/webSelfService'
import { getOrders } from '@/requests/order'
import { getSchedules } from '@/requests/schedule'
import { getAccessToken } from '@/services/cookieDealer'
import { OrderProps } from '@/modules/OrdersModule/types'
import { ScheduleProps } from '@/modules/ScheduleModule/types'
import { FAQModuleProps } from '@/modules/FAQModule/types'
import { WhatsappModuleProps } from '@/modules/WhatsappModule/types'
import { ReturnsAndExchangesModuleProps } from '@/modules/ReturnsAndExchangesModule/types'
import { OrdersModuleProps } from '@/modules/OrdersModule/types'
import { DEFAULT_MODULES, Module } from '@/constants/modules'

export interface BaseDataType {
  id: Module
  icon: string
  title: string
  isAvailable?: boolean
  isAvailableText?: string
  notAvailableText?: string
}

type ModuleDataType = ReturnsAndExchangesModuleProps | WhatsappModuleProps | FAQModuleProps | OrdersModuleProps | null

interface ModuleDataState {
  baseData?: BaseDataType[]
  moduleData: ModuleDataType
  whatsappIsOff: boolean
  isLogged: boolean
  ordersError: boolean
  ordersLoaded: boolean
  allOrders?: OrderProps[]
  schedulesError: boolean
  schedulesLoaded: boolean
  allSchedules?: ScheduleProps[]
}

export const useModuleData = (apiUrl: string, userId: string, module: Module) => {
  const [state, setState] = React.useState<ModuleDataState>({
    moduleData: null,
    whatsappIsOff: true,
    isLogged: false,
    ordersError: false,
    ordersLoaded: false,
    schedulesError: false,
    schedulesLoaded: false,
  })

  const updateState = React.useCallback((updates: Partial<ModuleDataState>) => {
    setState((prev) => ({ ...prev, ...updates }))
  }, [])

  const fetchBaseData = React.useCallback(async () => {
    try {
      const data = await getAllSectionsBase(apiUrl)
      const whatsappExists = data?.some((module: BaseDataType) => module.id === 'whatsapp')

      updateState({
        whatsappIsOff: !whatsappExists,
        baseData: [...DEFAULT_MODULES, ...data],
      })
    } catch (error) {
      console.error('Failed to fetch base data:', error)
    }
  }, [apiUrl, updateState])

  const fetchOrders = React.useCallback(async () => {
    if (!getAccessToken() || userId === '') {
      updateState({ ordersError: false, isLogged: false, ordersLoaded: true })
      return
    }

    try {
      const result = await getOrders(apiUrl, userId)

      if (result === null) {
        updateState({ ordersError: true, isLogged: true })
        return
      }

      updateState({
        ordersError: false,
        isLogged: true,
        allOrders: result.ordersInTransit,
        ordersLoaded: true,
      })
    } catch (error) {
      console.error('Failed to fetch orders:', error)
      updateState({ ordersError: true, isLogged: true })
    }
  }, [apiUrl, userId, updateState])

  const fetchSchedules = React.useCallback(async () => {
    if (!getAccessToken() || userId === '') {
      updateState({ schedulesError: false, isLogged: false, schedulesLoaded: true })
      return
    }

    try {
      const result = await getSchedules(apiUrl)

      if (result === null) {
        updateState({ schedulesError: true, isLogged: true })
        return
      }

      updateState({
        schedulesError: false,
        isLogged: true,
        allSchedules: result,
        schedulesLoaded: true,
      })
    } catch (error) {
      console.error('Failed to fetch schedules:', error)
      updateState({ schedulesError: true, isLogged: true })
    }
  }, [apiUrl, userId, updateState])

  const fetchModuleData = React.useCallback(async () => {
    try {
      if (module === 'orders') {
        await fetchOrders()
      } else if (module === 'schedules') {
        await fetchSchedules()
      } else if (module !== 'all' && module !== 'chat') {
        const data = await getSection(apiUrl, module)
        updateState({ moduleData: data })
      } else {
        updateState({ moduleData: null })
      }
    } catch (error) {
      console.error('Failed to fetch module data:', error)
    }
  }, [module, fetchOrders, fetchSchedules, apiUrl, updateState])

  React.useEffect(() => {
    fetchBaseData()
  }, [fetchBaseData])

  React.useEffect(() => {
    fetchModuleData()
  }, [fetchModuleData])

  return state
}
